import { useState, useEffect, useCallback, useRef } from 'react'
import * as AlertDialog from '@radix-ui/react-alert-dialog'
import { Layers, ChevronDown, Check, Plus, Trash2, Loader2 } from 'lucide-react'
import {
  listAppSessions,
  deleteAppSession,
  preflightDeleteWorkSession,
  type AppSession,
} from '../../lib/api'
import { confirmPreflightAction } from '../../lib/sessionHelpers'
import CreateWorkAreaModal from '../CreateWorkAreaModal'

// Work-area (Layers) dropdown in the workspace toolbar — the single place to
// switch, create, and delete work areas from inside the workspace. Creation
// uses the same named-input modal as the App Details page (no auto "tab-N"
// names); deletion runs the same safety preflight + confirm as App Details.
// "Session" is kept in code/API for backward compatibility (D-03); the
// user-facing strings use the neutral "work area" vocabulary (D-01).
export default function WorkAreaDropdown({
  appName,
  activeSession,
  onSelectSession,
}: {
  appName: string
  activeSession: string
  onSelectSession: (sessionName: string) => void
}) {
  const [sessions, setSessions] = useState<AppSession[]>([])
  const [isOpen, setIsOpen] = useState(false)
  // Menu position, measured from the trigger at open time and clamped to the
  // viewport. The trigger sits just after the app name in the breadcrumb —
  // near the LEFT edge on mobile — so a purely trigger-anchored panel clips
  // whichever side it extends toward (the old right-anchored panel ran off
  // the left edge on phones). Fixed positioning + clamping fits it always.
  const [menuPos, setMenuPos] = useState<{ top: number; left: number; width: number } | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [preflighting, setPreflighting] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<{ session: AppSession; description: string } | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const load = useCallback(async () => {
    try {
      const result = await listAppSessions(appName)
      setSessions(result.sessions)
    } catch {
      // Ignore fetch errors silently
    }
  }, [appName])

  // Fetch sessions on mount and every 30 seconds
  useEffect(() => {
    load()
    intervalRef.current = setInterval(load, 30000)
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [load])

  const activeSessions = sessions.filter(s => s.status !== 'archived')

  const requestDelete = useCallback(async (session: AppSession) => {
    setPreflighting(session.id)
    const description = await confirmPreflightAction(
      'Delete work area',
      () => preflightDeleteWorkSession(appName, session.id),
      'This removes the separate files for this work area.',
    )
    setPreflighting(null)
    setIsOpen(false)
    setDeleteError(null)
    setConfirmDelete({ session, description })
  }, [appName])

  const runDelete = useCallback(async () => {
    if (!confirmDelete) return
    setDeleting(true)
    setDeleteError(null)
    try {
      await deleteAppSession(appName, confirmDelete.session.id)
      // Deleting the work area you're standing in drops you back to main.
      if (confirmDelete.session.name === activeSession) {
        onSelectSession('main')
      }
      setConfirmDelete(null)
      await load()
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete work area')
      // Keep dialog open so the user can retry or cancel (D-09).
    } finally {
      setDeleting(false)
    }
  }, [appName, confirmDelete, activeSession, onSelectSession, load])

  const openMenu = useCallback(() => {
    if (isOpen) {
      setIsOpen(false)
      return
    }
    const rect = triggerRef.current?.getBoundingClientRect()
    if (rect && typeof window !== 'undefined') {
      // Panel width: up to 280px, never wider than the viewport minus margins.
      // Left edge: at the trigger when it fits, otherwise pulled back so the
      // panel's right edge stays 8px inside the viewport (and never < 8px).
      const width = Math.min(280, window.innerWidth - 16)
      const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8))
      setMenuPos({ top: rect.bottom + 4, left, width })
    } else {
      setMenuPos(null)
    }
    setIsOpen(true)
  }, [isOpen])

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        onClick={openMenu}
        title={`Work areas (current: ${activeSession})`}
        aria-label={`Work areas, currently ${activeSession}`}
        className="flex items-center gap-1.5 text-sm text-foreground/70 hover:text-foreground transition-colors px-1.5 py-0.5 rounded hover:bg-secondary"
      >
        <Layers className="w-3.5 h-3.5 flex-shrink-0" />
        <span className="hidden sm:inline">{activeSession}</span>
        <ChevronDown className="hidden sm:inline w-3 h-3" />
      </button>
      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          <div
            className={`z-50 bg-card border border-border rounded-lg shadow-lg py-1 ${
              menuPos ? 'fixed' : 'absolute right-0 top-full mt-1 min-w-[220px] max-w-[min(280px,calc(100vw-1rem))]'
            }`}
            style={menuPos ? { top: menuPos.top, left: menuPos.left, width: menuPos.width } : undefined}
          >
            {activeSessions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-muted-foreground">No work areas</div>
            ) : (
              activeSessions.map(session => (
                <div
                  key={session.id}
                  className="group flex items-center hover:bg-secondary transition-colors"
                >
                  <button
                    onClick={() => {
                      onSelectSession(session.name)
                      setIsOpen(false)
                    }}
                    className="flex-1 min-w-0 px-3 py-2 text-left text-sm flex items-center gap-2"
                  >
                    <Layers className="w-3.5 h-3.5 flex-shrink-0" />
                    <span className="truncate">{session.name}</span>
                    {session.name === activeSession && (
                      <Check className="w-3.5 h-3.5 text-primary ml-auto flex-shrink-0" />
                    )}
                  </button>
                  {session.name !== 'main' && (
                    <button
                      title={`Delete work area "${session.name}"`}
                      aria-label={`Delete work area ${session.name}`}
                      onClick={() => requestDelete(session)}
                      disabled={preflighting !== null}
                      className="p-2 mr-1 rounded text-muted-foreground hover:text-destructive transition-colors disabled:opacity-50 flex-shrink-0"
                    >
                      {preflighting === session.id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="w-3.5 h-3.5" />
                      )}
                    </button>
                  )}
                </div>
              ))
            )}
            <div className="border-t border-border mt-1 pt-1">
              <button
                onClick={() => {
                  setIsOpen(false)
                  setShowCreate(true)
                }}
                className="w-full px-3 py-2 text-left text-sm hover:bg-secondary transition-colors flex items-center gap-2 text-foreground/80"
              >
                <Plus className="w-3.5 h-3.5 flex-shrink-0" />
                New work area
              </button>
            </div>
          </div>
        </>
      )}

      {showCreate && (
        <CreateWorkAreaModal
          appName={appName}
          onClose={() => setShowCreate(false)}
          onCreated={(name) => {
            setShowCreate(false)
            load()
            onSelectSession(name)
          }}
        />
      )}

      <AlertDialog.Root
        open={!!confirmDelete}
        onOpenChange={(open) => {
          if (deleting) return
          if (!open) setConfirmDelete(null)
        }}
      >
        <AlertDialog.Portal>
          <AlertDialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
          <AlertDialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-border bg-card p-6 shadow-lg">
            <AlertDialog.Title className="text-lg font-semibold mb-2">
              Delete work area "{confirmDelete?.session.name}"?
            </AlertDialog.Title>
            <AlertDialog.Description className="text-sm text-muted-foreground whitespace-pre-wrap mb-4">
              {confirmDelete?.description}
            </AlertDialog.Description>
            {deleteError && (
              <div className="mb-4 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                {deleteError}
              </div>
            )}
            <div className="flex justify-end gap-3">
              <AlertDialog.Cancel asChild>
                <button
                  type="button"
                  className="px-4 py-2 rounded-lg bg-secondary hover:bg-secondary/80 transition-colors disabled:opacity-50"
                  disabled={deleting}
                >
                  Cancel
                </button>
              </AlertDialog.Cancel>
              <button
                type="button"
                onClick={runDelete}
                disabled={deleting}
                className="px-4 py-2 rounded-lg bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors disabled:opacity-50"
              >
                {deleting ? 'Deleting...' : deleteError ? 'Try again' : 'Delete'}
              </button>
            </div>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>
    </div>
  )
}
